'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  ContactAttribute,
  ContactAttributeList,
} from '@eliadmin/core/src/components/contact/contact-attribute-list';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { BiEnvelope, BiPhoneIncoming, BiTime } from 'react-icons/bi';
import { LanguageSelect } from './language-select';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'line-clamp-1 flex w-full flex-1 flex-row items-center justify-between overflow-hidden px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug} className="flex-1" prefetch={false}>
          <SheetClose
            className={cn(
              'h-7 text-left text-xl font-bold',
              layer === 1 && 'h-6 text-[16px] text-lg font-semibold',
              layer && layer > 1 && 'h-6 text-sm font-normal',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
  footerTime?: string;
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);

  const categories = props.categories || generalLayout.header?.categories;

  const contactData = [
    {
      icon: <BiPhoneIncoming size={32} />,
      value: generalLayout?.contact?.phone,
      label: t('text800'),
    },
    {
      icon: <BiEnvelope size={32} />,
      value: generalLayout?.contact?.email,
      label: t('text569'),
    },
    {
      icon: <BiTime size={32} />,
      value:
        (locale === 'sk' && 'Pon - Pia: 8:00 - 18:00') ||
        (locale === 'en' && 'Mon - Fri: 8:00 - 18:00') ||
        (locale === 'de' && 'Mo - Fr: 8:00 - 18:00'),
      label: t('text801'),
    },
  ];

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label={t('text3241')}
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          {HEADER_CONFIG?.sidebar?.triggerIcon}
        </SheetTrigger>
        <SheetContent
          aria-describedby={undefined}
          side={'right'}
          className={cn(
            '',
            'ea-mobile-sidebar__content',
            {
              'pt-20': !props?.firstSeparator,
            },
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'right-4 top-4',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <SheetTitle className="sr-only">{t('text3241')}</SheetTitle>
          {!!props.menu && (
            <div className="flex flex-col items-center justify-center gap-4">
              {props.menu?.map((item, index) => {
                const Comp = item.path
                  ? isRelativeHref(getSafeHref(item.path))
                    ? Link
                    : 'a'
                  : 'span';
                return (
                  <SheetClose key={index} asChild>
                    <Comp
                      href={item.path ? getSafeHref(item.path) : (undefined as any)}
                      className="flex w-full items-center justify-center"
                      prefetch={false}
                    >
                      <div className={cn('', 'ea-sidebar__menu-item')}>{item.name}</div>
                    </Comp>
                  </SheetClose>
                );
              })}
            </div>
          )}
          <Separator className={cn('', 'ea-sidebar__separator')} />
          <div className={cn('', 'ea-universal__contact-tab')}>
            {contactData?.map((item, index) => (
              <div key={index} className={cn('', 'ea-universal__contact-row')}>
                <div className={cn('', 'ea-universal__contact-icon')}>{item.icon}</div>
                <div className={cn('', 'ea-universal__contact-text')}>
                  <p>{item.label}</p>
                  <HtmlRendererRaw html={item.value ?? ''} />
                </div>
              </div>
            ))}
          </div>
          <Separator className={cn('', 'ea-sidebar__separator')} />
          <LanguageSelect />
          <Separator className={cn('', 'ea-sidebar__separator')} />
          <div>
            <SocialIcons className={{ icon: 'h-5 w-5', button: 'h-12 w-12', wrapper: '' }} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
